import { Component, computed, ElementRef, inject, input, OnInit, signal, viewChild } from '@angular/core';
import { DocumentHelper } from '../../helper/document';
import { CommonModule, DecimalPipe } from '@angular/common';
import { ParameterService, PaymentService } from '../../provider';
import { SpinnerComponent } from '../spinner/spinner.component';
import { of, switchMap, from, Observable, tap } from 'rxjs';

declare const paypal: any;
declare const google: any;

const PAYMENTSTATE_ERROR = 'ERROR';
const PAYMENTSTATE_DO_CAPTURE = 'DO_CAPTURE';
const PAYMENTSTATE_PAYER_ACTION_REQUIRED = 'PAYER_ACTION_REQUIRED';
let globalthis: any = undefined;

@Component({
  selector: 'lib-frontend-googlepay',
  templateUrl: './googlepay.html',
  styles: 'lib-frontend-spinner { margin: 0px auto; }',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent
  ],
  providers: [
    DecimalPipe,
  ]
})
export class Googlepay implements OnInit {
  data = input({} as any);
  paymentsClient: any;
  buttonContainer = viewChild<ElementRef>('buttonContainer');
  googlepayConfig: any;
  backUrl = computed(() => this.parameterService.getOverviewUrl());
  decimalPipe = inject(DecimalPipe);
  loading = signal(true);
  currentId = '';
  showError = signal(false);

  private parameterService = inject(ParameterService);
  private paymentService = inject(PaymentService);

  ngOnInit(): void {
    this.loadPaypalScript();
  }

  loadPaypalScript()
  {
    DocumentHelper.loadExternalScript(
      `https://www.paypal.com/sdk/js?components=googlepay&client-id=${this.data().client_id}`,
      this, 
      'loadGooglePayScript'
    );
  }

  loadGooglePayScript()
  {
    DocumentHelper.loadExternalScript(
      'https://pay.google.com/gp/p/js/pay.js',
      this,
      'onGooglePayLoaded'
    );
  }

  async getGooglePayConfig(){
    if(this.googlepayConfig === undefined){
      this.googlepayConfig = await paypal.Googlepay().config();
    }
    return this.googlepayConfig;
  }
  
  async getGooglePaymentDataRequest() {
    const {allowedPaymentMethods, merchantInfo, apiVersion, apiVersionMinor , countryCode} = await this.getGooglePayConfig();
    const baseRequest = {
      apiVersion,
      apiVersionMinor
    }
    const paymentDataRequest: any = Object.assign({}, baseRequest);
    paymentDataRequest.allowedPaymentMethods = allowedPaymentMethods;
    paymentDataRequest.transactionInfo = this.getGoogleTransactionInfo(countryCode);
    paymentDataRequest.merchantInfo = merchantInfo;
    paymentDataRequest.callbackIntents = ["PAYMENT_AUTHORIZATION"];
    return paymentDataRequest;
  }

  onPaymentAuthorized(paymentData: any): void {
    globalthis.processOrder(paymentData).subscribe(
      (state: string) => {
        switch(state)
        {
          case PAYMENTSTATE_DO_CAPTURE: globalthis.forwardToCapture();
            break;
          case PAYMENTSTATE_ERROR:
            globalthis.showError.set(true);
            break;
          case PAYMENTSTATE_PAYER_ACTION_REQUIRED: 
            globalthis.processPayerActionRequired().subscribe(() => globalthis.forwardToCapture());
            break;
        }
      }
    )
  }

  getGooglePaymentsClient() {
    if (this.paymentsClient === undefined) {
      globalthis = this;
      this.paymentsClient = new google.payments.api.PaymentsClient({
        environment: this.data().livemode * 1 === 1 ? 'PRODUCTION' : 'TEST',
        paymentDataCallbacks: {
          onPaymentAuthorized: this.onPaymentAuthorized
        }
      });
    }
    return this.paymentsClient;
  }

  async onGooglePayLoaded() {
    const paymentsClient = this.getGooglePaymentsClient();
    const { allowedPaymentMethods, apiVersion, apiVersionMinor } = await this.getGooglePayConfig();
    const globalthis = this;
    paymentsClient.isReadyToPay({allowedPaymentMethods, apiVersion, apiVersionMinor})
      .then(function(response: any) {
        if (response.result) {
          globalthis.addGooglePayButton();
        }
      })
      .catch(function(err: any) {
        // show error in developer console for debugging
        console.error(err);
      });
  }
  
  addGooglePayButton() {
    const paymentsClient = this.getGooglePaymentsClient();
    const globalthis = this;
    const button = paymentsClient.createButton({
      onClick: async function() {
          await globalthis.onGooglePaymentButtonClicked();
      }
    });
    this.buttonContainer()?.nativeElement.appendChild(button);
    this.loading.set(false);
  }

  getGoogleTransactionInfo(countryCode: string) {
    return {
      countryCode: countryCode,
      currencyCode: "EUR",
      totalPriceStatus: "FINAL",
      totalPrice: this.decimalPipe.transform(this.data().amount, '1.2-2', 'en-US'),
      totalPriceLabel: "Total"
    };
  }

  async onGooglePaymentButtonClicked() {
    const paymentDataRequest = await this.getGooglePaymentDataRequest();
    const paymentsClient = this.getGooglePaymentsClient();
    paymentsClient.loadPaymentData(paymentDataRequest);
  }

  switchProcessState(state: string): Observable<string>
  {
    return of(state === PAYMENTSTATE_ERROR || state === PAYMENTSTATE_PAYER_ACTION_REQUIRED ? state : PAYMENTSTATE_DO_CAPTURE);
  }

  processOrder(paymentData: any): Observable<string>
  {
    return this.paymentService.getCreateOrderForPaypal(this.parameterService.queryParams.channel_id, this.parameterService.queryParams.payment_id ?? '').pipe(
      switchMap(data => {
        this.currentId = data.data ?? '';
        if (this.currentId) {
          return from<string>(
            paypal.Googlepay().confirmOrder({
              orderId: this.currentId,
              paymentMethodData: paymentData.paymentMethodData
            })
          );
        }
        return of(PAYMENTSTATE_ERROR);
      }),
      switchMap(state => this.switchProcessState(state))
    );
  }

  forwardToCapture(): void
  {
    window.location.href = `${window.location.pathname}?channel=${this.parameterService.queryParams.channel_id}&payment=${this.parameterService.queryParams.payment_id ?? ''}&seccode=${this.currentId}`;
  }

  processPayerActionRequired() 
  {
    return from(paypal.Googlepay().initiatePayerAction({orderId: this.currentId}));
  }
}
